import Backdrop from "@mui/material/Backdrop";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import * as APIService from "../../common/api/index.js";
import AlertMessage from "../../common/components/AlertMessage";
import Button from "../../common/components/Button";
import { getSessionContext } from "../../common/utils/sessionContext";
import URL from "../../common/api/ConstantURL";
import "../../styles/addnotes.css";
import Header from "./Header";

const AddNotes = () => {
  const navigate = useNavigate();
  const { residentName, caregiverId, residentId, communityId } = getSessionContext();
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [showSendPopup, setShowSendPopup] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [previewNote, setPreviewNote] = useState("");
  const handoffTextareaRef = useRef(null);
  const saveWrapRef = useRef(null);
  const previewTextareaRef = useRef(null);

  const closeAlert = () => setAlertStatus("");
  const handleBack = () => navigate("/notes", { replace: true });

  const handleSendNote = async () => {
    if (!residentId) {
      setAlertStatus({
        message: "Resident is not selected.",
        status: "400",
        origin: "center",
      });
      return;
    }

    if (!description.trim()) {
      setAlertStatus({
        message: "Please enter hand off note.",
        status: "400",
        origin: "center",
      });
      return;
    }
    setPreviewNote(description.trim());
    setShowSendPopup(true);
  };

  const closeSendPopup = () => {
    setShowSendPopup(false);
  };

  const handleSuccessOk = () => {
    setShowSuccessDialog(false);
    navigate("/notes", { replace: true });
  };

  const submitSendNote = async (event) => {
    event?.preventDefault?.();
    const handoffNote = previewNote.trim();
    if (!handoffNote) {
      setAlertStatus({
        message: "Please enter hand off note.",
        status: "400",
        origin: "center",
      });
      return;
    }

    try {
      setLoading(true);
      const responseData = await APIService.postJsonData(URL.addNote, {
        caregiverId,
        clientId: residentId,
        description: handoffNote,
        communityId,
      });

      if (responseData?.status === "success" || responseData?.code === 200) {
        closeSendPopup();
        setSuccessMessage("Note sent successfully.");
        setShowSuccessDialog(true);
        setDescription("");
      } else {
        setAlertStatus({
          message: responseData?.message || "Failed to send note.",
          status: "400",
          origin: "center",
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      closeSendPopup();
      setAlertStatus({
        message: "Error while sending note.",
        status: "400",
        origin: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditInPopup = () => {
    setDescription(previewNote);
    closeSendPopup();
  };

  useEffect(() => {
    const resizeTextarea = () => {
      const textarea = handoffTextareaRef.current;
      const saveWrap = saveWrapRef.current;
      if (!textarea || !saveWrap) return;

      textarea.style.height = "auto";

      const minHeight = 120;
      const maxHeightByLayout = saveWrap.getBoundingClientRect().top - textarea.getBoundingClientRect().top - 12;
      const safeMaxHeight = Math.max(minHeight, Math.floor(maxHeightByLayout));
      const nextHeight = Math.min(textarea.scrollHeight, safeMaxHeight);

      textarea.style.height = `${Math.max(minHeight, nextHeight)}px`;
      textarea.style.overflowY = textarea.scrollHeight > safeMaxHeight ? "auto" : "hidden";
    };

    const rafId = window.requestAnimationFrame(resizeTextarea);
    window.addEventListener("resize", resizeTextarea);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resizeTextarea);
    };
  }, [description]);

  useEffect(() => {
    if (!showSendPopup) return;

    const resizePreviewTextarea = () => {
      const textarea = previewTextareaRef.current;
      if (!textarea) return;

      textarea.style.height = "auto";
      const minHeight = 120;
      const maxHeight = Math.floor(window.innerHeight * 0.42);
      const safeMaxHeight = Math.max(minHeight, maxHeight);
      const nextHeight = Math.min(textarea.scrollHeight, safeMaxHeight);
      textarea.style.height = `${Math.max(minHeight, nextHeight)}px`;
      textarea.style.overflowY = textarea.scrollHeight > safeMaxHeight ? "auto" : "hidden";
    };

    const rafId = window.requestAnimationFrame(resizePreviewTextarea);
    window.addEventListener("resize", resizePreviewTextarea);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resizePreviewTextarea);
    };
  }, [showSendPopup, previewNote]);

  return (
    <>
      <Header />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      <Dialog
        className="add-vitals-success-dialog"
        open={showSuccessDialog}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleSuccessOk();
        }}
        aria-labelledby="add-notes-success-title"
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "add-vitals-success-paper" }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}>
        <DialogContent className="add-vitals-success-content">
          <p id="add-notes-success-title" className="add-vitals-success-message">
            {successMessage}
          </p>
        </DialogContent>
        <DialogActions className="add-vitals-success-actions">
          <Button label="OK" onClick={handleSuccessOk} className="confirm-success-button" />
        </DialogActions>
      </Dialog>
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}
      <div className="home-container addnotes-home-container">
        <div className="home-inner-container resident-task-layout addnotes-inner">
          <div className="heading vitals-heading addnotes-page-header">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={handleBack} />
            <span>Add Note</span>
            <span></span>
          </div>

          <div className="task-container overflowAuto marginAuto col-11 col-sm-11 col-md-8 col-lg-4 col-xl-4 addnotes-scroll">
            <div className="addnotes-field">
              <label className="addnotes-label">Client Name</label>
              <input type="text" className="addnotes-input addnotes-input--readonly" value={residentName || ""} readOnly tabIndex={-1} />
            </div>

            <div className="addnotes-field">
              <label className="addnotes-label">Hand off Note:</label>
              <textarea ref={handoffTextareaRef} className="addnotes-input addnotes-input--auto-grow" rows={4} value={description} placeholder="Add hand off details..." onChange={(e) => setDescription(e.target.value.replace(/^\s+/, ""))} />
            </div>

            <div className="addnotes-save-wrap addnotes-save-wrap-push" ref={saveWrapRef}>
              <button type="button" className="addnotes-save-btn" onClick={handleSendNote} disabled={loading || !description.trim()}>
                {loading ? "Sending..." : "Send Note"}
              </button>
            </div>
          </div>
        </div>
        <Dialog className="popup addnotes-review-popup" open={showSendPopup} onClose={closeSendPopup} fullWidth maxWidth="sm">
          <DialogContent>
            <div className="addnotes-preview-title">Review Hand off Note</div>
            <textarea ref={previewTextareaRef} className="addnotes-input addnotes-preview-input" rows={5} value={previewNote} onChange={(e) => setPreviewNote(e.target.value.replace(/^\s+/, ""))} />
          </DialogContent>
          <DialogActions>
            <Button label="EDIT" onClick={handleEditInPopup} className="confirm-cancel-button" />
            <Button label="SEND" onClick={submitSendNote} className="confirm-success-button" />
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default AddNotes;
